import { useQuery } from '@tanstack/react-query';

import { fetchState } from './api';
import { isRetryablePortalError } from '../api/portalRequestError';
import { onboardQueryKeys } from '../onboardQueryKeys';

import type { UseQueryResult } from '@tanstack/react-query';

import type { IPortalState } from './types';

const MAX_RETRIES = 2;

interface UsePortalStateOptions {
  token: string;
  initialData?: IPortalState;
  enabled?: boolean;
}

// Candidate portal state. The initial value is already known server-side
// (passed in as `bootstrap` by Portal.tsx) so it is seeded via `initialData`
// and `staleTime: Infinity` - the query never refetches on its own. It only
// moves forward when a mutation explicitly invalidates or overwrites the
// `portalState` cache entry (see usePortalActions), matching the previous
// manual `reload()` helper. `enabled` is false for an already-known-expired
// token so an expired link never triggers a network call.
export function usePortalState({
  token,
  initialData,
  enabled = true
}: UsePortalStateOptions): UseQueryResult<IPortalState, Error> {
  return useQuery({
    queryKey: onboardQueryKeys.portalState(token),
    queryFn: async () => fetchState(token),
    initialData,
    enabled,
    staleTime: Infinity,
    retry: (failureCount, error) => isRetryablePortalError(error) && failureCount < MAX_RETRIES
  });
}
